import type { SelectionListItem } from '../../../../../../shared/selection-lists/types';
import type { MapPlotOptions } from '../../../PageConfigProvider/types/item';
import type { MetricValues } from '../../../PageConfigProvider/types/metric';
import { LocationType, type MapPoint } from '../types';
import { countryNameToIsoCode } from '../utils/country-codes';
import { normalizePointValue } from '../utils/normalization';
import type { ChoroplethLayerConfig } from './ChoroplethConfig';

export type ValueByLayerId = Map<string, Record<string, number>>;
export type TooltipByLayerId = Map<
  string,
  Record<string, Record<string, string | number>>
>;

export type Extent = { min: number; max: number };
export type LayerValues = Record<LocationType, number[]>;
export type LayerStatsByLocation = Record<
  LocationType,
  { values: number[]; extent: Extent | null }
>;

export interface ChoroplethDataOptions {
  msaSelectionList?: SelectionListItem<'msa'>[];
  stateSelectionList?: SelectionListItem<'state'>[];
  metric?: MetricValues;
  mapOptions?: MapPlotOptions;
}

export interface ChoroplethDataResult {
  values: {
    byLayer: ValueByLayerId;
    displayByLayer: ValueByLayerId;
    tooltips: TooltipByLayerId;
  };

  geography: {
    augmentedPoints: MapPoint[];
    hasNonMetroData: boolean;
    extraNonDataMsaIds: string[];
  };

  layers: LayerStatsByLocation;
}

export type IngestResult = {
  valueByLayerId: ValueByLayerId;
  displayValueByLayerId: ValueByLayerId;
  tooltipByLayerId: TooltipByLayerId;
  hasNonMetroData: boolean;
  countriesWithNonMetroData: Set<string>;
  metroIdsWithData: Set<string>;
  layerValues: LayerValues;
};

export function normalizeName(rawName: string): string {
  let name = rawName?.trim() || '';
  if (name.endsWith('Non-Metro')) {
    name = name.replace(/Non-Metro$/, '').trim();
  }
  if (name.endsWith('NonMetro')) {
    name = name.replace(/NonMetro$/, '').trim();
  }
  if (name.endsWith(', United States')) {
    name = name.replace(/, United States$/, '').trim();
  }
  return name;
}

// Add metro-areas without data to represent non-metro areas accurately as the difference between the country/state and the metro areas (instead of entire states/countries).
export function normalizeChoroplethData(
  points: MapPoint[],
  configs: ChoroplethLayerConfig[],
  options?: ChoroplethDataOptions
): ChoroplethDataResult {
  const { msaSelectionList, stateSelectionList, metric, mapOptions } =
    options ?? {};

  const pointsWithParents = augmentPointsWithParentCountryId(
    points,
    msaSelectionList,
    stateSelectionList
  );

  const {
    valueByLayerId,
    displayValueByLayerId,
    tooltipByLayerId,
    hasNonMetroData,
    layerValues,
  } = ingestPoints(pointsWithParents, configs, metric, mapOptions);

  const extraNonDataMsaIds = computeExtraNonDataMsaIds(
    pointsWithParents,
    msaSelectionList
  );

  return {
    values: {
      byLayer: valueByLayerId,
      displayByLayer: displayValueByLayerId,
      tooltips: tooltipByLayerId,
    },
    geography: {
      augmentedPoints: pointsWithParents,
      hasNonMetroData,
      extraNonDataMsaIds,
    },
    layers: buildLayerStats(layerValues),
  };
}

export function computeExtent(
  points: MapPoint[]
): { min: number; max: number } | null {
  const values = points
    .map((p) => p.value)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (!values.length) return null;
  return { min: Math.min(...values), max: Math.max(...values) };
}

function ingestPoints(
  points: MapPoint[],
  configs: ChoroplethLayerConfig[],
  metric?: MetricValues,
  mapOptions?: MapPlotOptions
): IngestResult {
  const valueByLayerId: ValueByLayerId = new Map();
  const displayValueByLayerId: ValueByLayerId = new Map();
  const tooltipByLayerId: TooltipByLayerId = new Map();

  configs.forEach((config) => {
    valueByLayerId.set(config.id, {});
    displayValueByLayerId.set(config.id, {});
    tooltipByLayerId.set(config.id, {});
  });

  const hasNonMetroData = true;
  const countriesWithNonMetroData: Set<string> = new Set();
  const metroIdsWithData: Set<string> = new Set();
  const layerValues = createEmptyLayerValues();

  for (const point of points) {
    const config = configs.find((c) => c.isApplicable(point));
    if (!config) continue;

    if (point.value === undefined || point.value === null) continue;

    const valueMap = valueByLayerId.get(config.id);
    const displayValueMap = displayValueByLayerId.get(config.id);
    const tooltipMap = tooltipByLayerId.get(config.id);
    if (!valueMap || !displayValueMap || !tooltipMap) continue;

    const label = normalizeName(point.label);
    const keyName = (() => {
      if (
        config.id === LocationType.METRO_AREA ||
        config.id === LocationType.CITY ||
        config.id === LocationType.STATE ||
        config.id === LocationType.COUNTY
      ) {
        return String(point.id);
      }
      if (config.id === LocationType.COUNTRY) {
        const iso = countryNameToIsoCode[label.toLowerCase()];
        return iso ?? label;
      }
      return label;
    })();

    const { value, displayValue } = normalizePointValue(
      point.value,
      metric,
      point.locationType,
      String(point.id),
      mapOptions,
      point.label
    );

    if (typeof value === 'number' && Number.isFinite(value)) {
      valueMap[keyName] = value;
      layerValues[config.id].push(value);
    }
    displayValueMap[keyName] = displayValue;
    if (point.tooltip) {
      tooltipMap[keyName] = { ...point.tooltip };
    }

    if (config.id === LocationType.METRO_AREA) {
      metroIdsWithData.add(String(point.id));
    }
  }

  return {
    valueByLayerId,
    displayValueByLayerId,
    tooltipByLayerId,
    hasNonMetroData,
    countriesWithNonMetroData,
    metroIdsWithData,
    layerValues,
  };
}

function augmentPointsWithParentCountryId(
  points: MapPoint[],
  msaSelectionList: SelectionListItem<'msa'>[] | undefined,
  stateSelectionList: SelectionListItem<'state'>[] | undefined
): MapPoint[] {
  const msaIdToCountryId: Record<string, string | number> = {};
  if (msaSelectionList?.length) {
    msaSelectionList.forEach((m) => {
      const countryId = Array.isArray(m.country) ? m.country[0] : m.country;
      if (countryId != null) {
        msaIdToCountryId[String(m.id)] = countryId;
      }
    });
  }

  const stateIdToCountryId: Record<string, string | number> = {};
  if (stateSelectionList?.length) {
    stateSelectionList.forEach((s) => {
      const countryId = Array.isArray(s.country) ? s.country[0] : s.country;
      if (countryId != null) {
        stateIdToCountryId[String(s.id)] = countryId;
      }
    });
  }

  return points.map((point) => {
    if (point.locationType === LocationType.METRO_AREA) {
      const countryId = msaIdToCountryId[String(point.id)];
      if (countryId != null) {
        return { ...point, parentCountryId: countryId };
      }
    }
    if (point.locationType === LocationType.STATE) {
      const countryId = stateIdToCountryId[String(point.id)];
      if (countryId != null) {
        return { ...point, parentCountryId: countryId };
      }
    }
    return point;
  });
}

function computeExtraNonDataMsaIds(
  points: MapPoint[],
  msaSelectionList: SelectionListItem<'msa'>[] | undefined
): string[] {
  if (!msaSelectionList?.length) return [];

  const metroIdsWithData: Set<string> = new Set();
  const countryIds: Set<string> = new Set();

  for (const p of points) {
    const hasValue = p.value !== undefined && p.value !== null;
    if (hasValue && p.locationType === LocationType.METRO_AREA) {
      metroIdsWithData.add(String(p.id));
      if (p.parentCountryId != null) {
        countryIds.add(String(p.parentCountryId));
      }
    }
  }

  const extraIds: string[] = [];
  msaSelectionList.forEach((m: SelectionListItem<'msa'>) => {
    const msaId = String(m.id);
    const rawCountry = m.country;
    const countryId = Array.isArray(rawCountry) ? rawCountry[0] : rawCountry;

    if (
      countryId != null &&
      countryIds.has(String(countryId)) &&
      !metroIdsWithData.has(msaId)
    ) {
      extraIds.push(msaId);
    }
  });

  return extraIds;
}

function createEmptyLayerValues(): LayerValues {
  return {
    [LocationType.REGION]: [],
    [LocationType.COUNTRY]: [],
    [LocationType.STATE]: [],
    [LocationType.COUNTY]: [],
    [LocationType.METRO_AREA]: [],
    [LocationType.CITY]: [],
  };
}

function buildLayerStats(layerValues: LayerValues): LayerStatsByLocation {
  const computeExtentFrom = (vals: number[]): Extent | null =>
    vals.length ? { min: Math.min(...vals), max: Math.max(...vals) } : null;

  return {
    [LocationType.REGION]: {
      values: layerValues[LocationType.REGION],
      extent: computeExtentFrom(layerValues[LocationType.REGION]),
    },
    [LocationType.COUNTRY]: {
      values: layerValues[LocationType.COUNTRY],
      extent: computeExtentFrom(layerValues[LocationType.COUNTRY]),
    },
    [LocationType.STATE]: {
      values: layerValues[LocationType.STATE],
      extent: computeExtentFrom(layerValues[LocationType.STATE]),
    },
    [LocationType.COUNTY]: {
      values: layerValues[LocationType.COUNTY],
      extent: computeExtentFrom(layerValues[LocationType.COUNTY]),
    },
    [LocationType.METRO_AREA]: {
      values: layerValues[LocationType.METRO_AREA],
      extent: computeExtentFrom(layerValues[LocationType.METRO_AREA]),
    },
    [LocationType.CITY]: {
      values: layerValues[LocationType.CITY],
      extent: computeExtentFrom(layerValues[LocationType.CITY]),
    },
  };
}
